import { ConstraintInstance, ExpressionConstraint } from './constraint.js';
import bigInt, { BigInteger } from 'big-integer';
import { PropertyPath } from './engine.js';

export type TypeReference = string;

export enum TypeKind {
  Primitive = 'Primitive',
  List = 'List',
  Enum = 'Enum',
  Object = 'Object',
  Union = 'Union'
}

export enum SystemType {
  Boolean = 'Boolean',
  Number = 'Number',
  String = 'String',
  MLString = 'MLString',
  BigNumber = 'BigNumber'
}

export const primitiveTypes = Object.values(SystemType).map(name => name.toLowerCase());

interface IEquable<T> {
  eq(subject: IEquable<T>): boolean;
}

export function equableTypeGuard(subject: unknown): subject is IEquable<unknown> {
  return typeof subject['eq'] === 'function';
}

export abstract class Type {
  public name: string;
  public kind: TypeKind;

  constructor(name: string, kind: TypeKind) {
    this.kind = kind;
    this.name = name;
  }

  getTypeReference(): TypeReference {
    return this.name;
  }
}

export class PrimitiveType extends Type {
  public valueType: SystemType;

  constructor(name: string, valueType: SystemType) {
    super(name, TypeKind.Primitive);
    this.valueType = valueType;
  }
}

export function primitiveTypeTypeGuard(subject: Type): subject is PrimitiveType {
  return subject.kind === TypeKind.Primitive;
}

export class EnumType extends Type {
  public values: number[] | string[];
  //TODO do we need to specify type for enum? or just available values?
  public valueType: SystemType.String | SystemType.MLString | SystemType.Number;

  constructor(name: string, valueType: SystemType.String | SystemType.MLString | SystemType.Number, values: number[] | string[]) {
    super(name, TypeKind.Enum);
    this.valueType = valueType;
    this.values = values;
  }
}

export class ListType extends Type {
  public elementType: TypeReference;

  constructor(name: string, elementType: TypeReference = '', public nullable = false) {
    super(name, TypeKind.List);
    this.elementType = elementType;
  }
}

export interface PropertyOptions {
  needSource?: boolean | string[] | 'specific';
  constraints?: ConstraintInstance[];
  availability?: ExpressionConstraint[];
  default?: any;
  sourceOptions?: SourceOptions;

  [x: string]: any;
}

export interface SourceOptions {
  checker: string;
}

export class Property {
  public name: string;
  public type: TypeReference;
  public opts: PropertyOptions = {
    needSource: false,
    constraints: [],
    availability: [],
    sourceOptions: null
  };

  constructor(name: string, type: TypeReference, opts: PropertyOptions = {}) {
    this.name = name;
    this.type = type;
    Object.assign(this.opts, opts);
  }
}

export interface ExternalField<T = any> {
  name: string;
  type: TypeReference;
  properties: PropertyPath[];
  default: T;
}

export interface CompatibilityTableDescriptor {
  name: string,
  properties: PropertyPath[];
}

export class ObjectType extends Type {
  public properties: Property[];
  private propertyMap: { [x: string]: any } = {};

  constructor(name: string,
              properties: Property[],
              public readonly components: TypeReference[] = [],
              public readonly externals: ExternalField[] = [],
              public readonly compatibilityTables: CompatibilityTableDescriptor[] = []
  ) {
    super(name, TypeKind.Object);
    this.properties = properties;
    for (let property of properties) {
      if (this.propertyMap[property.name]) {
        throw new Error('duplicate property:' + name);
      }
      this.propertyMap[property.name] = property;
    }
  }

  getProperty(name: string): Property | undefined {
    // Workaround for propertyPathWalker to be able to access ._id for Options, Products
    if (name === '_id') {
      return {
        name: '_id',
        opts: {},
        type: 'string'
      };
    }
    return this.propertyMap[name];
  }
}

export type BoolOperation = '<' | '>' | '<=' | '>=' | '==' | '!=';

export class BigNumber implements IEquable<BigNumber> {
  private value: BigInteger;
  private dotPosition = 0;
  private dotChar = '.';

  static zero() {
    return new BigNumber(0);
  }

  constructor(value?: string | number) {
    if (!arguments.length) {
      this.value = bigInt(0);
      return;
    }
    value = value.toString();
    let dotPosition = 0;
    let dotFound = false;
    let index = 0;
    let numberValue = '';
    while (index < value.length) {
      const char = value[index];
      if (char === this.dotChar) {
        if (dotFound) {
          throw new Error('Invalid number');
        } else {
          dotFound = true;
          dotPosition = value.length - index - 1;
        }
      } else {
        numberValue += char;
      }
      index++;
    }
    this.dotPosition = dotPosition;
    this.value = bigInt(numberValue);
    this.prettify();
  }

  private static create(value: BigInteger, dotPosition: number): BigNumber {
    const self = new BigNumber();
    self.value = value;
    self.dotPosition = dotPosition;
    self.prettify();
    return self;
  }

  plus(value: BigNumber) {
    const dotPosition = Math.max(this.dotPosition, value.dotPosition);
    let a = this.value;
    let b = value.value;
    if (this.dotPosition < dotPosition) {
      const multiplier = dotPosition - this.dotPosition;
      a = this.value.multiply(bigInt('10').pow(multiplier));
    }
    if (value.dotPosition < dotPosition) {
      const multiplier = dotPosition - value.dotPosition;
      b = value.value.multiply(bigInt('10').pow(multiplier));
    }

    return BigNumber.create(a.plus(b), dotPosition);
  }

  minus(value: BigNumber) {
    return this.plus(value.uminus());
  }

  uminus() {
    return BigNumber.create(this.value.multiply(-1), this.dotPosition);
  }

  toNumberParts(): [string] | [string, string] {
    const str = this.value.toString();
    if (this.dotPosition === 0) {
      return [str];
    }

    return [str.slice(0, -this.dotPosition), str.slice(-this.dotPosition)];
  }

  toString() {
    const numParts = this.toNumberParts();
    if (numParts.length === 1) {
      return numParts[0];
    }
    return numParts[0] + '.' + numParts[1];
  }

  eq(value: BigNumber);
  eq(value: BigNumber | number) {
    const b = typeof value === 'number' ? new BigNumber(value) : value;
    return this.is('==', b);
  }

  mulBy(quantity: number) {
    if (quantity === 0) {
      return BigNumber.zero();
    }
    let result: BigNumber = this;
    const iterations = Math.abs(quantity);
    for (let i = 0; i < iterations - 1; i++) {
      result = result.plus(this);
    }
    return (quantity < 0) ? result.uminus() : result;
  }

  multiply(a: BigNumber): BigNumber {
    const value = this.value.multiply(a.value);
    const dotPosition = this.dotPosition + a.dotPosition;
    return BigNumber.create(value, dotPosition);
  }

  private prettify() {
    while (this.dotPosition > 0 && this.value.mod(10).eq(0)) {
      this.value = this.value.divide(10);
      this.dotPosition--;
    }
  }

  isNegative(): boolean {
    return !this.value.eq(0) && this.value.isNegative();
  }

  isPositive(): boolean {
    return !this.value.eq(0) && this.value.isPositive();
  }

  is(operator: BoolOperation, value: BigNumber) {
    switch (operator) {
      case '<':
        return this.boolOperator(value, (a, b) => a.lesser(b));
      case '>':
        return this.boolOperator(value, (a, b) => a.greater(b));
      case '<=':
        return this.boolOperator(value, (a, b) => a.lesserOrEquals(b));
      case '>=':
        return this.boolOperator(value, (a, b) => a.greaterOrEquals(b));
      case '==':
        return this.boolOperator(value, (a, b) => a.equals(b));
      case '!=':
        return this.boolOperator(value, (a, b) => !a.eq(b));
    }
  }

  boolOperator(value: BigNumber, operator: (c1: BigInteger, c2: BigInteger) => boolean) {
    let c1: BigNumber = this;
    let c2 = value;
    while (!(c1.dotPosition === 0 && c2.dotPosition === 0)) {
      c1 = c1.mulBy10();
      c2 = c2.mulBy10();
    }
    return operator(c1.value, c2.value);
  }

  mulBy10(): BigNumber {
    let value = this.value;
    let dotPosition = this.dotPosition;
    if (dotPosition === 0) {
      value = value.multiply(10);
    } else {
      dotPosition--;
    }
    return BigNumber.create(value, dotPosition);
  }

  round(): BigNumber {
    const parts = this.toNumberParts();
    if (parts.length === 1) {
      return BigNumber.create(this.value, this.dotPosition);
    }

    const [integer, fraction] = parts;
    const firstFractionDigit = Number(fraction[0]);
    let integerNumber = bigInt(integer);
    if (!Number.isNaN(firstFractionDigit) && firstFractionDigit >= 5) {
      integerNumber = integerNumber.plus(bigInt('1'));
    }

    return BigNumber.create(integerNumber, 0);
  }
}

export class MLString implements IEquable<MLString> {
  static empty = new MLString('');

  constructor(public value: string | { [k: string]: string }) {
  }

  hashCode() {
    if (typeof this.value === 'string') {
      return this.value;
    }
    return Object.entries(this.value)
      .reduce((acc, [key, value]) => acc + `$__${key}__:${value};`, '');
  }

  eq(mlstring: MLString) {
    return this.hashCode() == mlstring.hashCode();
  }
}

export class UnionType extends Type {
  constructor(name: string, private _types: TypeReference[]) {
    super(name, TypeKind.Union);
  }

  get types(): Readonly<TypeReference[]> {
    return this._types;
  }
}

export function objectTypeTypeGuard(type: Type): type is ObjectType {
  return type.kind === TypeKind.Object;
}
